package com.swetdot.swetgram.ui.design

import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.text.KeyboardActionScope
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.*
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.viewinterop.HtmlElementView
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.KeyboardEvent

internal actual val usesBrowserNativeTextInput: Boolean = true

internal fun createBrowserTextElement(singleLine: Boolean): HTMLElement =
    (document.createElement(if (singleLine) "input" else "textarea") as HTMLElement).apply {
        setAttribute("data-swetgram-input", "")
        setAttribute("autocomplete", "off")
        setAttribute("aria-label", "Text input")
        style.apply {
            setProperty("box-sizing", "border-box")
            setProperty("border", "0")
            setProperty("border-radius", "0")
            setProperty("padding", "0")
            setProperty("margin", "0")
            setProperty("outline", "none")
            setProperty("background", "transparent")
            setProperty("font-family", "system-ui, sans-serif")
            setProperty("font-size", "16px") // Avoid Safari's focus zoom.
            setProperty("resize", "none")
            setProperty("-webkit-appearance", "none")
            setProperty("touch-action", "auto")
            setProperty("user-select", "text")
            setProperty("-webkit-user-select", "text")
        }
        if (this is HTMLTextAreaElement) rows = 1
    }

private fun HTMLElement.textValue(): String = when (this) {
    is HTMLInputElement -> value
    is HTMLTextAreaElement -> value
    else -> error("Not a text editor")
}

internal fun HTMLElement.syncTextValue(value: String) {
    // Assigning value on every recomposition resets the caret and breaks IME/autofill.
    if (textValue() == value) return
    when (this) {
        is HTMLInputElement -> this.value = value
        is HTMLTextAreaElement -> this.value = value
    }
}

internal fun HTMLElement.browserTextHeight(lineHeight: Float, maxLines: Int): Float {
    val previousHeight = style.height
    try {
        // Measure content rather than the old height, including after clearing a draft.
        style.height = "0px"
        return scrollHeight.toFloat().coerceIn(lineHeight, lineHeight * maxLines.coerceIn(1, 12))
    } finally {
        style.height = previousHeight
    }
}

private fun Color.css(): String = "rgba(${(red * 255).toInt()},${(green * 255).toInt()},${(blue * 255).toInt()},$alpha)"

@OptIn(ExperimentalComposeUiApi::class)
@Composable
internal actual fun BrowserNativeTextInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier,
    textStyle: TextStyle,
    enabled: Boolean,
    singleLine: Boolean,
    maxLines: Int,
    keyboardOptions: KeyboardOptions,
    keyboardActions: KeyboardActions,
    visualTransformation: VisualTransformation,
    placeholder: String,
    onFocusChanged: (Boolean) -> Unit,
) {
    val change by rememberUpdatedState(onValueChange)
    val focusChange by rememberUpdatedState(onFocusChanged)
    val actions by rememberUpdatedState(keyboardActions)
    val options by rememberUpdatedState(keyboardOptions)
    val lineHeight = textStyle.lineHeight.value.takeIf { it.isFinite() }?.coerceAtLeast(22f) ?: 22f
    val fontSize = textStyle.fontSize.value.takeIf { it.isFinite() }?.coerceAtLeast(16f) ?: 16f
    var height by remember(singleLine) { mutableStateOf(if (singleLine) lineHeight.coerceAtLeast(36f) else lineHeight) }
    var composing by remember(singleLine) { mutableStateOf(false) }
    var editRevision by remember(singleLine) { mutableStateOf(0) }
    val editorRef = remember(singleLine) { arrayOfNulls<HTMLElement>(1) }
    val color = textStyle.color.takeIf { it != Color.Unspecified } ?: Color.White

    // Reconcile after Compose has processed onValueChange, even if validation
    // rejected it without changing the model. A frame timer can run too early
    // on a slow phone and replace an accepted keystroke with the previous value.
    key(editRevision) {
        SideEffect {
            if (!composing) editorRef[0]?.let { editor ->
                editor.syncTextValue(value)
                if (!singleLine) height = editor.browserTextHeight(lineHeight, maxLines)
            }
        }
    }

    key(singleLine) {
        HtmlElementView(
            modifier = modifier.fillMaxWidth().height(height.dp),
            factory = {
                createBrowserTextElement(singleLine).apply {
                    editorRef[0] = this
                    fun accept() {
                        if (!composing) {
                            change(textValue())
                            editRevision++
                        }
                    }
                    addEventListener("input", { accept() })
                    addEventListener("change", { accept() }) // Browser autofill.
                    addEventListener("compositionstart", { composing = true })
                    addEventListener("compositionend", { composing = false; accept() })
                    addEventListener("focus", { focusChange(true) })
                    addEventListener("blur", { focusChange(false) })
                    addEventListener("keydown", { event ->
                        val keyEvent = event as KeyboardEvent
                        if (keyEvent.key == "Enter" && singleLine && !composing) {
                            val scope = object : KeyboardActionScope {
                                override fun defaultKeyboardAction(imeAction: ImeAction) {
                                    if (imeAction == ImeAction.Next || imeAction == ImeAction.Previous) {
                                        val editors = document.querySelectorAll("[data-swetgram-input]:not(:disabled)")
                                        val index = (0 until editors.length).firstOrNull { editors.item(it) === this@apply } ?: return
                                        val step = if (imeAction == ImeAction.Next) 1 else -1
                                        (editors.item(index + step) as? HTMLElement)?.focus()
                                    } else if (imeAction == ImeAction.Done) blur()
                                }
                            }
                            val action = options.imeAction
                            val handler = when (action) {
                                ImeAction.Done -> actions.onDone
                                ImeAction.Go -> actions.onGo
                                ImeAction.Next -> actions.onNext
                                ImeAction.Previous -> actions.onPrevious
                                ImeAction.Search -> actions.onSearch
                                ImeAction.Send -> actions.onSend
                                else -> null
                            }
                            keyEvent.preventDefault()
                            if (handler != null) handler(scope) else scope.defaultKeyboardAction(action)
                        }
                    })
                }
            },
            update = { editor ->
                if (!composing) editor.syncTextValue(value)
                val password = keyboardOptions.keyboardType == KeyboardType.Password || keyboardOptions.keyboardType == KeyboardType.NumberPassword
                if (editor is HTMLInputElement) {
                    editor.disabled = !enabled
                    val type = if (password && visualTransformation != VisualTransformation.None) "password" else "text"
                    if (editor.type != type) editor.type = type
                } else if (editor is HTMLTextAreaElement) editor.disabled = !enabled
                editor.setAttribute("aria-label", placeholder.ifBlank { "Text input" })
                editor.setAttribute("inputmode", when (keyboardOptions.keyboardType) {
                    KeyboardType.Email -> "email"
                    KeyboardType.Phone -> "tel"
                    KeyboardType.Number, KeyboardType.NumberPassword -> "numeric"
                    KeyboardType.Decimal -> "decimal"
                    KeyboardType.Uri -> "url"
                    else -> "text"
                })
                editor.setAttribute("enterkeyhint", when (keyboardOptions.imeAction) {
                    ImeAction.Next -> "next"
                    ImeAction.Previous -> "previous"
                    ImeAction.Done -> "done"
                    ImeAction.Search -> "search"
                    ImeAction.Send -> "send"
                    ImeAction.Go -> "go"
                    else -> "enter"
                })
                editor.setAttribute("autocapitalize", if (password) "off" else when (keyboardOptions.capitalization) {
                    androidx.compose.ui.text.input.KeyboardCapitalization.Characters -> "characters"
                    androidx.compose.ui.text.input.KeyboardCapitalization.Words -> "words"
                    androidx.compose.ui.text.input.KeyboardCapitalization.Sentences -> "sentences"
                    else -> "off"
                })
                editor.setAttribute("autocorrect", if (password || keyboardOptions.autoCorrectEnabled == false) "off" else "on")
                editor.setAttribute("spellcheck", if (password) "false" else "true")
                editor.style.setProperty("font-size", "${fontSize}px")
                editor.style.setProperty("line-height", "${lineHeight}px")
                editor.style.setProperty("color", color.css())
                editor.style.setProperty("caret-color", color.css())
            },
            onRelease = { editor ->
                if (editorRef[0] === editor) editorRef[0] = null
                editor.syncTextValue("")
            },
        )
    }
}
